import VehcileDefaults from 'domain/vehicleDefaults.json';
import uuid from 'uuid/v4';
import moment from 'moment';
import * as I18n from 'circe/I18n';

const findComponent = (components, name, attribute) => {
  const found = components.find((component) => component.types.indexOf(name) !== -1);

  if (!found) {
    return;
  }

  if (attribute) {
    return found[attribute];
  }

  return found;
};

export const getInSeconds = (hms) => {
  const a = hms.split(':'); // split it at the colons
  // minutes are worth 60 seconds. Hours are worth 60 minutes.
  const seconds = +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
  return seconds;
};

const getFormattedAddress = (address) => {
  const components = address.address_components;

  const city =
    findComponent(components, 'locality', 'long_name') ||
    findComponent(components, 'administrative_area_level_2', 'long_name');
  const country = findComponent(components, 'country', 'long_name');
  const countryCode = findComponent(components, 'country', 'short_name');
  const street = findComponent(components, 'route', 'long_name');
  const postalCode = findComponent(components, 'postal_code', 'long_name');
  const provinceCode = findComponent(components, 'administrative_area_level_1', 'long_name');

  const lat = address.geometry.location.lat();
  const lng = address.geometry.location.lng();
  const { formatted_address } = address;
  const addressParts = address.address_components.reduce((acc, row) => {
    acc[row.types[0]] = row;
    return acc;
  }, {});

  return {
    address: formatted_address,
    city,
    coordinates: {
      longitude: lng,
      latitude: lat,
    },
    type: 'Point',
    country,
    countryCode,
    postalCode,
  };
};
const mapVehicles = (vehicles) => {
  const output = [];
  if (vehicles) {
    for (const vehicleType of vehicles) {
      const temp = {
        dimensions: {
          height: vehicleType.capacity.height,
          length: vehicleType.capacity.length,
          width: vehicleType.capacity.width,
        },
        sizeInTones: vehicleType.maxWeight,
        type: vehicleType.type,
      };
      output.push(temp);
    }
  }
  return output;
};

const getTimes = (time) => {
  const times = {};

  time?.forEach((key) => {
    if (
      (key.administrativeTimeInMin || key.administrativeTimeInMin === 0) &&
      (key.waitingTimeInMin || key.waitingTimeInMin === 0) &&
      (key.TimeInMinBox || key.TimeInMinBox === 0) &&
      (key.TimeInMinPallet || key.TimeInMinPallet === 0)
    ) {
      let label;
      if (key.account) {
        label = key.strategyType ? `${key.account}#${key.strategyType}` : key.account;
      } else {
        label = key.strategyType ? `DEFAULT#${key.strategyType}` : 'DEFAULT';
      }
      times[label] = {
        administrativeTimeInMin: key.administrativeTimeInMin,
        waitingTimeInMin: key.waitingTimeInMin,
        variableTimePerUnitOfMeasureInSec: {
          box: key.TimeInMinBox * 60,
          pallet: key.TimeInMinPallet * 60,
        },
      };
    }
  });

  return times;
};

const getFleets = (addressPreferences) => {
  const fleets = {};

  addressPreferences?.forEach((key) => {
    let label;
    if (key.strategyType) {
      label = key.strategyType ? `${key.strategyType}` : null;
    }
    if (label !== undefined && label !== null) {
      fleets[label] = key.fleets ? key.fleets.split(',') : [];
    }
  });

  return fleets;
};
const getCouriersAccounts = (addressPreferences) => addressPreferences?.split(',');

const getWorkingHours = (workingHours) => {
  return workingHours?.map((item) => ({
    begin: moment(`2011-10-05T${item.startHours}z`).toISOString(),
    end: moment(`2011-10-05T${item.endHours}z`).toISOString(),
  }));
};

export const mapToServer = (values) => {
  const address = getFormattedAddress(values.address[0]);

  return {
    code: values.code,
    // domain: "iatco",
    accountId: values.accountId,
    ownerId: values.profileId,
    type: values.type,
    name: values.name,
    operationTypes: values.operationTypes,
    location: address,
    workingHours: {
      pickup: getWorkingHours(values.pickupWorkingHours),
      dropOff: getWorkingHours(values.dropOffWorkingHours),
      returnLoad: getWorkingHours(values.returnLoad),
      returnUnload: getWorkingHours(values.returnUnload),
    },
    description: values.description,
    loadingTime: getTimes(values.LoadingTimes),
    unloadingTime: getTimes(values.UnloadingTimes),
    reloadingTime: getTimes(values.ReloadingTimes),
    returnLoadingTime: getTimes(values.returnLoadingTimes),
    returnUnloadingTime: getTimes(values.returnUnloadingTimes),
    contactInfo: {
      primary: {
        name: values.contact,
        email: values.email,
        mobile: values.mobile,
      },
    },
    requirements: {
      customer_type: values.customer_type,
      priority: values.priority,
      po_requirement: values.po_requirement,
    },
    operationTypes: values.operationTypes,
    addressPreferences: {
      fleetAccountIds: getCouriersAccounts(values.workingCourier),
      workingFleetIds: getFleets(values.AddressPreferences),
      shippingOption: values.shippingOption,
      preferredVehicles: values?.vehicles,
    },
  };
};

export const mapToUpdate = (values) => {
  const address =
    typeof values.address === 'string' ? values.location : getFormattedAddress(values.address[0]);
  return {
    code: values.code,
    _id: values.id,
    accountId: values.accountId,
    ownerId: values.ownerId,
    type: values.type,
    name: values.name,
    operationTypes: values.operationTypes,
    location: address,
    workingHours: {
      pickup: getWorkingHours(values.pickupWorkingHours),
      dropOff: getWorkingHours(values.dropOffWorkingHours),
      returnLoad: getWorkingHours(values.returnLoad),
      returnUnload: getWorkingHours(values.returnUnload),
    },
    description: values.description,
    loadingTime: getTimes(values.LoadingTimes),
    unloadingTime: getTimes(values.UnloadingTimes),
    reloadingTime: getTimes(values.ReloadingTimes),
    returnLoadingTime: getTimes(values.returnLoadingTimes),
    returnUnloadingTime: getTimes(values.returnUnloadingTimes),
    contactInfo: {
      primary: {
        name: values.contact,
        email: values.email,
        mobile: values.mobile,
      },
    },
    requirements: {
      customer_type: values.customer_type,
      priority: values.priority,
      po_requirement: values.po_requirement,
    },
    operationTypes: values.operationTypes,
    addressPreferences: {
      fleetAccountIds: getCouriersAccounts(values.workingCourier),
      workingFleetIds: getFleets(values.AddressPreferences),
      shippingOption: values.shippingOption,
      preferredVehicles: values?.vehicles,
    },
  };
};

export const formatExposeFilters = (filters) => {
  const result = {
    ...filters,
    type: filters.type ? filters.type.split(',') : null,
    city: filters.city ? filters.city.split(',') : null,
    codes: filters.code ? filters.code.split(',') : null,
    names: filters.names ? filters.names.split(',') : null,
    code: null,
  };
  for (const key in result) {
    if (result.hasOwnProperty(key) && result[key] === null) {
      delete result[key];
    }
  }
  return result;
};
export const noNegative = (value) => {
  if (value < 0) {
    return I18n.str('noNegative', 'Value cannot be negative');
  }
};

export const filterVehicleTypes = (vehicleTypes) =>
  vehicleTypes
    .filter((vehicleType) => {
      if (vehicleType.type) {
        return true;
      }

      return false;
    })
    .map((vehicleType) => ({
      value: vehicleType,
      label: vehicleType.type,
    }));
